import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MapPin, Phone } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyCallBar } from "@/components/StickyCallBar";
import { LeadForm } from "@/components/LeadForm";
import { Button } from "@/components/ui/button";
import { SERVICES_SEO, CITIES } from "@/data/servicePages";

const EASE = [0.16, 1, 0.3, 1];
const slugify = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function setMetaDescription(content) {
  let tag = document.querySelector('meta[name="description"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "description");
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

export default function CityHubPage() {
  const { citySlug } = useParams();
  const city = CITIES.find((c) => c.slug === citySlug);

  useEffect(() => {
    if (!city) return;
    document.title = `Chimney Services in ${city.name}, OR | Exotictileandmasonry`;
    setMetaDescription(
      `Chimney sweep, chimney repair, crowns, caps, flashing, tuckpointing & emergency escrow chimney service in ${city.name}, Oregon. $249 sweep special. Call 971-255-3874.`
    );
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-city";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ChimneySweep",
      name: "Exotictileandmasonry",
      telephone: "+1-971-255-3874",
      url: "https://pdx-chimney-pro.emergent.host",
      priceRange: "$249",
      areaServed: { "@type": "City", name: `${city.name}, OR` },
    });
    document.head.appendChild(script);
    return () => {
      document.title =
        "Exotictileandmasonry | $249 Chimney Sweep + Free Inspection — Greater PDX";
      document.getElementById("ld-city")?.remove();
    };
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  return (
    <div data-testid="city-hub-page">
      <Header />
      <main className="pt-16">
        <section className="bg-accent py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              data-testid="back-home-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back to home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                Service Area
              </p>
              <h1
                data-testid="page-title"
                className="font-heading text-4xl sm:text-5xl tracking-tighter font-extrabold text-white leading-tight"
              >
                Chimney Services in {city.name}, OR
              </h1>
              <p className="text-base md:text-lg text-white/75 leading-relaxed mt-6 max-w-2xl">
                Sweeping, repair, crowns, caps, flashing, tuckpointing, and emergency
                escrow service — all from one local crew. {city.blurb}
              </p>
              <div className="flex flex-wrap gap-2 mt-8" data-testid="hub-neighborhoods">
                {city.neighborhoods.map((n) => (
                  <span
                    key={n}
                    className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                  >
                    <MapPin className="w-3.5 h-3.5 text-primary" strokeWidth={2} />
                    {n}
                  </span>
                ))}
              </div>
              <Button
                asChild
                data-testid="page-call-button"
                className="btn-press mt-10 bg-primary hover:bg-[#A93A24] text-white font-bold rounded-md h-12 px-8"
              >
                <a href="tel:+19712553874" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" strokeWidth={2} /> Call 971-255-3874
                </a>
              </Button>
            </motion.div>
          </div>
        </section>

        <section className="py-24" data-testid="hub-services">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-foreground mb-12">
              Every Chimney Service We Offer in {city.name}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SERVICES_SEO.map((s, i) => (
                <motion.div
                  key={s.slug}
                  data-testid={`hub-service-card-${s.slug}`}
                  className="card-lift bg-card border border-border rounded-md p-6 flex flex-col"
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                >
                  <h3 className="font-heading text-lg tracking-tight font-bold text-foreground mb-2">
                    {s.name}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                    {s.bullets[0]}
                  </p>
                  <Link
                    to={`/services/${s.slug}/${city.slug}`}
                    data-testid={`hub-service-link-${s.slug}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary hover:gap-3 transition-[gap]"
                  >
                    {s.name} in {city.name} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                Free Quote
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-foreground">
                Chimney Work in {city.name}? Get a Free Quote
              </h2>
              <p className="text-base text-muted-foreground mt-4 leading-relaxed max-w-md">
                Send your name and number and we'll call you back with a straight answer
                and a fair price. Emergency escrow? Callback within 24 hours, service
                within 48.
              </p>
            </div>
            <div className="bg-card border border-border rounded-md p-8" data-testid="page-quote-form-card">
              <LeadForm variant="quote" />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallBar />
    </div>
  );
}
